import { forwardRef, useCallback, useId, useMemo, useState } from 'react';
import { Box, FormControl, FormGroup, FormHelperText, InputLabel } from '@mui/material';

import BaseFieldContainer from '../common/baseFieldContainer';
import { FCheckbox } from '../fCheckbox';
import { hasRenderableHelperText } from '../utils/general';
import {
  CHECKBOX_GROUP_FORM_CONTROL_LABEL_SX,
  FORM_CONTROL_LABEL_ROW_SX,
  FORM_CONTROL_LABEL_VERTICAL_SX,
} from './constants';
import {
  buildCheckboxGroupAriaIds,
  buildCheckboxGroupLayoutSx,
  buildInputLabelSx,
  getCheckboxGroupOutlinedBoxSx,
  mergeCheckboxGroupBorderSx,
  mergeSxProps,
} from './utils/general';

import type { ChangeEvent } from 'react';
import type { FCheckboxGroupProps } from './types';

const FCheckboxGroup = forwardRef<HTMLDivElement, FCheckboxGroupProps>(
  function FCheckboxGroup(props, ref) {
    const {
      children,
      options,
      value,
      defaultValue = [],
      onChange,
      checkboxSize = 'small',
      label,
      helperText,
      error = false,
      disabled = false,
      fullWidth = false,
      required = false,
      color,
      margin = 'none',
      size: formControlSize,
      variant: formControlVariant = 'outlined',
      inputLabelProps,
      sx: boxSx,
      checkboxGroupSx,
      name,
      ...restGroup
    } = props;

    const { row = false, id: idProp, role, ...restFormGroup } = restGroup;
    const { sx: inputLabelUserSx, ...inputLabelRest } = inputLabelProps ?? {};
    const generatedId = useId();
    const [uncontrolledValue, setUncontrolledValue] = useState<ReadonlyArray<string>>(defaultValue);

    const selectedValues = value ?? uncontrolledValue;
    const selectedValueSet = useMemo(() => new Set(selectedValues), [selectedValues]);
    const showRequiredIndicator = Boolean(required) && !disabled;
    const helperPresent = hasRenderableHelperText(helperText);
    const ariaIds = useMemo(
      () => buildCheckboxGroupAriaIds(idProp, generatedId),
      [idProp, generatedId],
    );

    const outlinedBoxSx = useMemo(
      () => mergeCheckboxGroupBorderSx(getCheckboxGroupOutlinedBoxSx({ error, disabled }), boxSx),
      [boxSx, error, disabled],
    );

    const checkboxGroupLayoutSx = useMemo(
      () =>
        buildCheckboxGroupLayoutSx({
          row,
          checkboxGroupSx,
          formControlLabelRowSx: FORM_CONTROL_LABEL_ROW_SX,
          formControlLabelVerticalSx: FORM_CONTROL_LABEL_VERTICAL_SX,
        }),
      [row, checkboxGroupSx],
    );

    const handleCheckboxChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const changedValue = event.target.value;
        const nextValues = checked
          ? [
              ...selectedValues.filter((selectedValue) => selectedValue !== changedValue),
              changedValue,
            ]
          : selectedValues.filter((selectedValue) => selectedValue !== changedValue);

        if (value == null) {
          setUncontrolledValue(nextValues);
        }

        onChange?.(event, nextValues, changedValue);
      },
      [onChange, selectedValues, value],
    );

    return (
      <FormControl
        error={error}
        disabled={disabled}
        fullWidth={fullWidth}
        required={required}
        color={color}
        margin={margin}
        size={formControlSize}
        variant={formControlVariant}
        component="div"
      >
        <BaseFieldContainer
          required={required}
          disabled={disabled}
          error={error}
          fullWidth={fullWidth}
          alwaysRenderContainer
        >
          {label ? (
            <InputLabel
              {...inputLabelRest}
              id={ariaIds.labelId}
              error={error}
              disabled={disabled}
              required={showRequiredIndicator ? false : required}
              shrink
              sx={buildInputLabelSx(inputLabelUserSx)}
            >
              {label}
            </InputLabel>
          ) : null}
          <Box sx={outlinedBoxSx}>
            <FormGroup
              ref={ref}
              id={idProp}
              role={role ?? 'group'}
              aria-labelledby={label ? ariaIds.labelId : undefined}
              aria-describedby={helperPresent ? ariaIds.helperTextId : undefined}
              row={row}
              {...restFormGroup}
              sx={checkboxGroupLayoutSx}
            >
              {options != null
                ? options.map((option) => {
                    const { formControlLabelProps, ...restCheckboxProps } =
                      option.checkboxProps ?? {};
                    const { sx: formControlLabelSx, ...restFormControlLabelProps } =
                      formControlLabelProps ?? {};

                    return (
                      <Box
                        key={option.value}
                        sx={{
                          mt: row ? 0 : 1,
                          mr: row ? 1 : 0,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FCheckbox
                          {...restCheckboxProps}
                          name={name}
                          value={option.value}
                          checked={selectedValueSet.has(option.value)}
                          onChange={handleCheckboxChange}
                          size={checkboxSize}
                          label={option.label}
                          error={error}
                          disabled={disabled || option.disabled}
                          formControlLabelProps={{
                            ...restFormControlLabelProps,
                            sx: mergeSxProps(
                              CHECKBOX_GROUP_FORM_CONTROL_LABEL_SX,
                              formControlLabelSx,
                            ),
                          }}
                        />
                      </Box>
                    );
                  })
                : children}
            </FormGroup>
          </Box>
        </BaseFieldContainer>
        {helperPresent ? (
          <FormHelperText
            id={ariaIds.helperTextId}
            error={error}
            disabled={disabled}
            component="p"
            sx={{ margin: 0, textAlign: 'start', width: '100%' }}
          >
            {helperText}
          </FormHelperText>
        ) : null}
      </FormControl>
    );
  },
);

export default FCheckboxGroup;
